/** @jsx isolateComponent */

import { forwardRef, useImperativeHandle, useRef } from 'react';
import type { Ref } from 'react';

import { Input } from '../../forms/Input';
import type { InputProps } from '../../forms/Input';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';

// `{ focus: () => void }` is a workaround for `useImperativeHandle`.
type InputElement =
  | HTMLInputElement
  | HTMLTextAreaElement
  | { focus: () => void };

export type Props = Omit<InputProps, 'children' | 'size'> & {
  /** Controls how text is automatically capitalized as it's entered
   * by the user
   */
  autoCapitalize?: 'on' | 'off' | 'words' | 'characters';
  /**
   * Browser `autocomplete` form field attribute.
   */
  autoComplete?: string;
  /** Activates automatic correct while the user is editing the field
   * (used by Safari)
   */
  autoCorrect?: 'on' | 'off';
  /**
   * Ref that points to the rendered `input` or `textarea`.
   */
  forwardedRef?: Ref<InputElement>;
  /**
   * Should this component render in its full width?
   */
  isFullWidth?: boolean;
  /**
   * Whether the input accepts multiple lines of text.
   * Renders an `input` if `false` and a `textarea` if true`.
   */
  isMultiline?: boolean;
  /**
   * HTML inputmode for controlling which keyboard shows on mobile
   * https://css-tricks.com/everything-you-ever-wanted-to-know-about-inputmode/
   * https://html.spec.whatwg.org/multipage/interaction.html#input-modalities:-the-inputmode-attribute
   */
  inputMode?:
    | 'decimal'
    | 'email'
    | 'none'
    | 'numeric'
    | 'search'
    | 'tel'
    | 'text'
    | 'url';
  /**
   * Limits the maximum number of characters within the input.
   */
  maxLength?: number;
  /**
   * Indicates in which directions a `textarea` can be resized.
   * Only works when `isMultiline={true}`!
   */
  resize?: 'horizontal' | 'vertical' | 'both' | 'none';
  /**
   * Indicates how many rows should render by default for a `textarea`s.
   * Only works when `isMultiline={true}`!
   */
  rows?: number;
  /**
   * The rendered size of the input.
   * @deprecated 'small' (44px) is now the only supported size
   */
  size?: 'small' | 'medium';
  /** Use spelling and grammar checks in the text input */
  spellCheck?: 'true' | 'false';
  /**
   * Indicates the type of the input so the browser can provide a better
   * experience to users. Only used for `input` fields and *not* `textarea`s,
   * so is affected by `isMultiline`.
   */
  type?: 'email' | 'password' | 'search' | 'tel' | 'text';
};

const Styles = labelStyles('TextInput', {
  input: (props: Props) => [
    // FireFox puts an arbitrary min-width on the input otherwise
    { minWidth: 1 },
    props.isMultiline &&
      withModifier('multiline', {
        height: 'auto',
      }),

    props.isFullWidth &&
      withModifier('fullwidth', {
        width: '100%',
      }),

    props.resize &&
      withModifier(`resize-${props.resize}`, {
        resize: props.resize,
      }),
  ],
});

const forwardedTextInput = (props: Props, ref: Ref<InputElement>) => (
  <BaseTextInput {...props} forwardedRef={ref} />
);

/**
 * Renders either an `input` or `textarea` element, depending on the
 * `isMultiline` prop. This is a bare-bones implementation without
 * any special handling for events.
 */
const BaseTextInput = (props: Props) => {
  const inputRef = useRef<InputElement>(null);
  useImperativeHandle(props.forwardedRef, () => ({
    focus() {
      // eslint-disable-next-line no-unused-expressions
      inputRef.current?.focus();
    },
  }));

  const Tag = props.isMultiline ? 'textarea' : 'input';
  return (
    <Input
      {...props}
      renderIconAfter={props.isMultiline ? undefined : props.renderIconAfter}
      renderIconBefore={props.isMultiline ? undefined : props.renderIconBefore}
    >
      {(inputProps) => (
        <Tag
          {...inputProps}
          autoCapitalize={props.autoCapitalize}
          autoCorrect={props.autoCorrect}
          css={[inputProps.css, Styles.input(props)]}
          inputMode={props.inputMode}
          // Struggled to get `InputElement` to work here, but it's
          // properly typed elsewhere.
          ref={inputRef as Ref<any>}
          rows={props.isMultiline ? props.rows : undefined}
          spellCheck={props.spellCheck}
          type={!props.isMultiline ? props.type : undefined}
          value={inputProps.value}
          onChange={inputProps.onChange}
        />
      )}
    </Input>
  );
};

BaseTextInput.defaultProps = {
  isMultiline: false,
  type: 'text',
  isFullWidth: false,
  size: 'small',
};

export const TextInput = forwardRef(forwardedTextInput);
